import React, { useContext, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CustomModal, Switcher, UiButton } from '@shared/ui';

import cls from './FirstStep.module.css';
import { DateInput } from '../../../../../components/DateInput/DateInput';
import { Form } from '../../../../../components/UI/Form/Form';
import { FormBlock } from '../../../../../components/UI/Form/FormBlock/FormBlock';
import { Select } from '../../../../../components/UI/Select/Select';
import { fetchDataForSecondStep } from '../../../../../store/slices/paymentSlice/action';
import { ModalContext } from '../AddPaymentModal';
import { TabPanel } from '../TabPanel/TabPanel';

export const FirstStep = ({
    modalState,
    setModalState,
    onSwitcherChange,
    onHideModal,
    isCanDeleteUnsettled,
    deleteUndistributedPayment,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const partnersSelectList = useSelector((state) => state.paymentReducer.partnersSelectList);
    const allIncome = useSelector((state) => state.paymentReducer.incomeList);
    const estimatesSelectList = useSelector((state) => state.paymentReducer.estimatesSelectList);

    const accountsSelectList = useSelector((state) => state.paymentReducer.accountsSelectList);

    const {
        selectedAccount,
        selectAccountHandler,
        formState,
        onPaymentDateChange,
        onAccrualDateChange,
        errors,
        setErrors,
        mode,
    } = useContext(ModalContext);

    const [activeTab, setActiveTab] = useState(modalState.mode);

    useEffect(() => {
        setModalState((prev) => ({ ...prev, mode: activeTab }));
    }, [activeTab]);

    const onSubmitButton = async () => {
        const nextStep = selectedAccount.account.integration ? 3 : 2;
        if (
            selectedAccount.account.integration &&
            modalState.mode === 'debit' &&
            modalState.addPaymentMode !== 'distribution'
        ) {
            setErrors((prevState) => ({
                ...prevState,
                account: t('FirstStep.onSubmitButton.setErrors.account'),
            }));
        } else if (!formState.account) {
            setErrors((prevState) => ({
                ...prevState,
                account: t('FirstStep.onSubmitButton.setErrors'),
            }));
        } else {
            setErrors((prevState) => ({ ...prevState, account: null }));
            if (partnersSelectList.length && estimatesSelectList.length && allIncome.length) {
                setModalState((prevState) => ({ ...prevState, step: nextStep }));
            } else {
                loader.show();
                await dispatch(fetchDataForSecondStep());

                await setModalState((prevState) => ({ ...prevState, step: nextStep }));

                await loader.hide();
            }
        }
    };

    let isDisabledAccount = false;
    let isDisabledDate = false;

    if (mode === 'distribution') {
        isDisabledAccount = true;
        isDisabledDate = true;
    }
    return (
        <>
            <CustomModal.Body>
                <TabPanel
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isDisabled={
                        mode === 'distribution' || modalState.addPaymentMode === 'applications'
                    }
                />
                <Form>
                    <div className={classNames(cls.formBlock)}>
                        <div>
                            {modalState.mode === 'debit'
                                ? t('FirstStep.formBlock.debit')
                                : t('FirstStep.formBlock.credit')}
                        </div>
                        <FormBlock>
                            <Select
                                isSmartPosition
                                selected={selectedAccount}
                                onChange={selectAccountHandler}
                                placeholder={t('FirstStep.formBlock.selectedAccount.placeholder')}
                                title={t('FirstStep.formBlock.selectedAccount.title')}
                                isRequired
                                options={accountsSelectList}
                                error={errors.account}
                                isDisabled={isDisabledAccount}
                                isAccount
                            />
                        </FormBlock>
                        <FormBlock>
                            <div className={classNames(cls.blockWrapper)}>
                                <DateInput
                                    title={t('FirstStep.formBlock.paymentDate.title')}
                                    isRequired
                                    date={formState.paymentDate}
                                    inventoryGuid="inventoryGuid"
                                    parameterName="paymentDate"
                                    newCallback={onPaymentDateChange}
                                    isDisabled={isDisabledDate}
                                    options={{
                                        startDate: formState.paymentDate,
                                        singleDatePicker: true,
                                        showDropdowns: true,
                                        autoApply: true,
                                    }}
                                />
                            </div>
                            <div className={classNames(cls.blockWrapper)}>
                                <DateInput
                                    title={t('FirstStep.formBlock.accrualDate.title')}
                                    isRequired
                                    date={formState.accrualDate}
                                    inventoryGuid="inventoryGuid"
                                    parameterName="accrualDate"
                                    isDisabled={isDisabledDate}
                                    newCallback={onAccrualDateChange}
                                    options={{
                                        startDate: formState.accrualDate,
                                        singleDatePicker: true,
                                        showDropdowns: true,
                                        autoApply: true,
                                    }}
                                />
                            </div>
                        </FormBlock>
                        {mode === 'add' && modalState.mode === 'debit' ? null : (
                            <div className={classNames(cls.switcherWrapper)}>
                                <Switcher
                                    classes={cls.switcher}
                                    onClick={onSwitcherChange}
                                    isActive={modalState.isBetweenAccount}
                                    title={t('FirstStep.formBlock.isBetweenAccount.title')}
                                    isDisabled={
                                        modalState.addPaymentMode === 'applications' ||
                                        modalState.addPaymentMode === 'repeat'
                                    }
                                />
                            </div>
                        )}
                    </div>
                </Form>
            </CustomModal.Body>
            <CustomModal.Footer>
                <div className={classNames(cls.footer)}>
                    <UiButton
                        btnText={t('FirstStep.footer.cancel')}
                        btnColor="secondary"
                        onClick={onHideModal}
                    />

                    {isCanDeleteUnsettled && (
                        <UiButton
                            btnColor="danger"
                            btnText={t('button.delete.text')}
                            className={cls.deleteButton}
                            onClick={() => deleteUndistributedPayment?.()}
                        />
                    )}
                    <UiButton btnText={t('FirstStep.footer.next')} onClick={onSubmitButton} />
                </div>
            </CustomModal.Footer>
        </>
    );
};
